import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Calendar,
  BookOpen,
  Award,
  Edit3,
  Save,
  Camera,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { reporterProfile } from '../../data/reporterData';
import { useLanguage } from '../../context/LanguageContext';

const content = {
  ne: {
    title: 'प्रोफाइल',
    editProfile: 'प्रोफाइल सम्पादन गर्नुहोस्',
    saveChanges: 'परिवर्तन सुरक्षित गर्नुहोस्',
    name: 'नाम',
    email: 'इमेल',
    phone: 'फोन नम्बर',
    specialization: 'विशेषज्ञता',
    bio: 'बायो / परिचय',
    bioPlaceholder: 'आफ्नो बारेमा संक्षिप्त परिचय...',
    joined: 'सामेल मिति',
    totalArticles: 'कुल लेख',
    role: 'भूमिका',
    roleLabel: 'रिपोर्टर',
    profileSaved: 'प्रोफाइल सफलतापूर्वक सुरक्षित गरियो!',
    cancel: 'रद्द गर्नुहोस्',
  },
  en: {
    title: 'Profile',
    editProfile: 'Edit Profile',
    saveChanges: 'Save Changes',
    name: 'Name',
    email: 'Email',
    phone: 'Phone Number',
    specialization: 'Specialization',
    bio: 'Bio / Introduction',
    bioPlaceholder: 'Brief introduction about yourself...',
    joined: 'Joined',
    totalArticles: 'Total Articles',
    role: 'Role',
    roleLabel: 'Reporter',
    profileSaved: 'Profile saved successfully!',
    cancel: 'Cancel',
  },
};

const ReporterProfile = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = content[language] || content.en;
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || reporterProfile.name,
    email: user?.email || reporterProfile.email,
    phone: reporterProfile.phone,
    specialization: reporterProfile.specialization,
    bio: reporterProfile.bio,
  });

  const handleSave = () => {
    setSaved(true);
    setIsEditing(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const initials = profile.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-extrabold text-char-900">{t.title}</h2>

      {saved && (
        <div className="rounded-2xl bg-primary-100 px-4 py-3 text-sm font-bold text-primary">
          {t.profileSaved}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Avatar card */}
        <div className="rounded-3xl border border-char-100 bg-white p-6 shadow-card text-center">
          <div className="relative mx-auto h-24 w-24">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary text-3xl font-extrabold text-white">
              {initials}
            </div>
            {isEditing && (
              <label className="absolute bottom-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow-sm border border-char-100 text-char-500 hover:text-primary transition-colors">
                <Camera size={14} />
                <input type="file" accept="image/*" className="hidden" />
              </label>
            )}
          </div>
          <h3 className="mt-4 text-lg font-extrabold text-char-900">{profile.name}</h3>
          <p className="text-sm text-char-500">{t.roleLabel}</p>

          <div className="mt-6 space-y-3 border-t border-char-100 pt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-char-400">{t.joined}</span>
              <span className="font-semibold text-char-700">{reporterProfile.joinedDate}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-char-400">{t.totalArticles}</span>
              <span className="font-semibold text-char-700">{reporterProfile.totalArticles}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-char-400">{t.role}</span>
              <span className="rounded-full bg-primary-100 px-3 py-0.5 text-xs font-extrabold text-primary">
                {t.roleLabel}
              </span>
            </div>
          </div>

          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-char-200 py-2.5 text-sm font-extrabold text-char-600 transition-all hover:border-primary-200 hover:bg-primary-50 hover:text-primary"
            >
              <Edit3 size={15} />
              {t.editProfile}
            </button>
          )}
        </div>

        {/* Profile details */}
        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-char-100 bg-white p-6 shadow-card">
            {isEditing ? (
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.name}</label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.email}</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.phone}</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.specialization}</label>
                    <input
                      type="text"
                      value={profile.specialization}
                      onChange={(e) => setProfile({ ...profile, specialization: e.target.value })}
                      className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.bio}</label>
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    rows={4}
                    placeholder={t.bioPlaceholder}
                    className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-extrabold text-white shadow-green transition-all hover:-translate-y-0.5 hover:bg-primary-500 hover:shadow-green-hover"
                  >
                    <Save size={15} />
                    {t.saveChanges}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="rounded-xl border-2 border-char-200 px-6 py-2.5 text-sm font-extrabold text-char-600 transition-all hover:border-char-300 hover:bg-char-50"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-2xl bg-char-50 px-4 py-3">
                    <Mail size={18} className="text-char-400 shrink-0" />
                    <div>
                      <p className="text-xs text-char-400">{t.email}</p>
                      <p className="text-sm font-bold text-char-800">{profile.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-char-50 px-4 py-3">
                    <Phone size={18} className="text-char-400 shrink-0" />
                    <div>
                      <p className="text-xs text-char-400">{t.phone}</p>
                      <p className="text-sm font-bold text-char-800">{profile.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-char-50 px-4 py-3">
                    <Award size={18} className="text-char-400 shrink-0" />
                    <div>
                      <p className="text-xs text-char-400">{t.specialization}</p>
                      <p className="text-sm font-bold text-char-800">{profile.specialization}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-char-50 px-4 py-3">
                    <BookOpen size={18} className="text-char-400 shrink-0" />
                    <div>
                      <p className="text-xs text-char-400">{t.bio}</p>
                      <p className="text-sm font-bold text-char-800">{profile.bio}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporterProfile;
